// src/pages/statistics/BuyerTypeStatistics.tsx
// Вкладка «По типу покупателя» в «Общей статистике»: 4 раскрывающихся списка
// (клиенты / распространители / API / распространители, которым выдаём билеты вручную),
// внутри — мероприятия и города. У ручных распространителей — окно «Добавить проданные
// билеты»: отметка мест «оплачено у распространителя» (заказ при этом не становится оплаченным).
import React, { useCallback, useEffect, useState } from "react";
import Button from "../../components/ui/Button";
import {
    BuyerTypeDistributorRow,
    BuyerTypeMetrics,
    BuyerTypeManualSegment,
    BuyerTypeRegularSegment,
    BuyerTypeTour,
    BuyerTypesStatisticsResponse,
    ManualDistributorEvent,
    ManualDistributorMetrics,
    ManualDistributorRow,
    ManualDistributorSeat,
    getBuyerTypesStatisticsRequest,
    getManualDistributorEventsRequest,
    getManualDistributorSeatsRequest,
    setManualDistributorSeatsPaidRequest,
} from "../../services/api/statistics.api";
import { formatDate, formatMoney } from "./statsFormat";

const toggleInSet = (set: Set<string>, key: string): Set<string> => {
    const next = new Set(set);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    return next;
};

const cityTitle = (name: string, startAt: string | null) =>
    startAt ? `${name} — ${formatDate(startAt)}` : name;

// ---------- Таблица туров -> городов для клиентов / распространителей / API ----------

const RegularToursTable: React.FC<{ segment: BuyerTypeRegularSegment }> = ({ segment }) => {
    const [openTours, setOpenTours] = useState<Set<string>>(new Set());

    if (segment.tours.length === 0) {
        return <p className="stats-cell-muted buyer-type-empty">Продаж нет.</p>;
    }

    const cells = (m: BuyerTypeMetrics) => (
        <>
            <td className="stats-cell-left">{m.orders_count}</td>
            <td className="stats-cell-left">{m.tickets_sold}</td>
            <td className="stats-cell-left">{formatMoney(m.sum_amount)}</td>
            <td className="stats-cell-left">{formatMoney(m.paid_sum)}</td>
            <td className="stats-cell-left">{formatMoney(m.unpaid_sum)}</td>
        </>
    );

    return (
        <div className="table-wrap">
            <table className="data-table stats-table buyer-type-table">
                <thead>
                <tr>
                    <th>Мероприятие / город</th>
                    <th>Заказов</th>
                    <th>Мест</th>
                    <th>На сумму</th>
                    <th>Оплачено</th>
                    <th>Не оплачено</th>
                </tr>
                </thead>
                <tbody>
                {segment.tours.map((tour) => {
                    const isOpen = openTours.has(tour.id);
                    return (
                        <React.Fragment key={tour.id}>
                            <tr className="stats-period-row">
                                <td className="stats-row-name">
                                    <button
                                        type="button"
                                        className={`stats-period-toggle ${isOpen ? "stats-period-toggle-open" : ""}`}
                                        onClick={() => setOpenTours((prev) => toggleInSet(prev, tour.id))}
                                    >
                                        {tour.name}
                                    </button>
                                </td>
                                {cells(tour)}
                            </tr>
                            {isOpen &&
                                tour.cities.map((city) => (
                                    <tr className="stats-event-city-row" key={city.id}>
                                        <td className="stats-row-name">{cityTitle(city.name, city.start_at)}</td>
                                        {cells(city)}
                                    </tr>
                                ))}
                        </React.Fragment>
                    );
                })}
                </tbody>
            </table>
        </div>
    );
};

// ---------- Распространители / API: распространитель -> тур -> город ----------

const regularCells = (m: BuyerTypeMetrics) => (
    <>
        <td className="stats-cell-left">{m.orders_count}</td>
        <td className="stats-cell-left">{m.tickets_sold}</td>
        <td className="stats-cell-left">{formatMoney(m.sum_amount)}</td>
        <td className="stats-cell-left">{formatMoney(m.paid_sum)}</td>
        <td className="stats-cell-left">{formatMoney(m.unpaid_sum)}</td>
    </>
);

const RegularDistributorsTable: React.FC<{ distributors: BuyerTypeDistributorRow[] }> = ({ distributors }) => {
    const [open, setOpen] = useState<Set<string>>(new Set());

    if (distributors.length === 0) {
        return <p className="stats-cell-muted buyer-type-empty">Продаж нет.</p>;
    }

    return (
        <div className="table-wrap">
            <table className="data-table stats-table buyer-type-table">
                <thead>
                <tr>
                    <th>Распространитель / мероприятие / город</th>
                    <th>Заказов</th>
                    <th>Мест</th>
                    <th>На сумму</th>
                    <th>Оплачено</th>
                    <th>Не оплачено</th>
                </tr>
                </thead>
                <tbody>
                {distributors.map((d) => {
                    const isOpen = open.has(d.id);
                    return (
                        <React.Fragment key={d.id}>
                            <tr className="stats-period-row">
                                <td className="stats-row-name">
                                    <button
                                        type="button"
                                        className={`stats-period-toggle ${isOpen ? "stats-period-toggle-open" : ""}`}
                                        onClick={() => setOpen((prev) => toggleInSet(prev, d.id))}
                                        title={d.email || undefined}
                                    >
                                        {d.name}
                                    </button>
                                </td>
                                {regularCells(d)}
                            </tr>
                            {isOpen &&
                                d.tours.map((tour) => {
                                    const key = `${d.id}:${tour.id}`;
                                    const tourOpen = open.has(key);
                                    return (
                                        <React.Fragment key={key}>
                                            <tr className="stats-event-row">
                                                <td className="stats-row-name">
                                                    <button
                                                        type="button"
                                                        className={`stats-period-toggle ${tourOpen ? "stats-period-toggle-open" : ""}`}
                                                        onClick={() => setOpen((prev) => toggleInSet(prev, key))}
                                                    >
                                                        {tour.name}
                                                    </button>
                                                </td>
                                                {regularCells(tour)}
                                            </tr>
                                            {tourOpen &&
                                                tour.cities.map((city) => (
                                                    <tr className="stats-event-city-row" key={city.id}>
                                                        <td className="stats-row-name">
                                                            {cityTitle(city.name, city.start_at)}
                                                        </td>
                                                        {regularCells(city)}
                                                    </tr>
                                                ))}
                                        </React.Fragment>
                                    );
                                })}
                        </React.Fragment>
                    );
                })}
                </tbody>
            </table>
        </div>
    );
};

// ---------- Таблица ручных распространителей: распространитель -> тур -> город ----------

const manualCells = (m: ManualDistributorMetrics) => (
    <>
        <td className="stats-cell-left">{m.issued_tickets}</td>
        <td className="stats-cell-left">{m.tickets_sold}</td>
        <td className="stats-cell-left">{formatMoney(m.sum_amount)}</td>
        <td className="stats-cell-left">{m.unsold_tickets}</td>
        <td className="stats-cell-left">{formatMoney(m.paid_sum)}</td>
    </>
);

const ManualDistributorsTable: React.FC<{
    segment: BuyerTypeManualSegment;
    onAddSold: (distributor: ManualDistributorRow) => void;
}> = ({ segment, onAddSold }) => {
    const [open, setOpen] = useState<Set<string>>(new Set());

    if (segment.distributors.length === 0) {
        return (
            <p className="stats-cell-muted buyer-type-empty">
                Нет распространителей с отметкой «Выдаём билеты вручную». Её можно поставить в
                карточке распространителя в разделе «Пользователи».
            </p>
        );
    }

    const renderTour = (distributorId: string, tour: BuyerTypeTour<ManualDistributorMetrics>) => {
        const key = `${distributorId}:${tour.id}`;
        const isOpen = open.has(key);
        return (
            <React.Fragment key={key}>
                <tr className="stats-event-row">
                    <td className="stats-row-name">
                        <button
                            type="button"
                            className={`stats-period-toggle ${isOpen ? "stats-period-toggle-open" : ""}`}
                            onClick={() => setOpen((prev) => toggleInSet(prev, key))}
                        >
                            {tour.name}
                        </button>
                    </td>
                    {manualCells(tour)}
                </tr>
                {isOpen &&
                    tour.cities.map((city) => (
                        <tr className="stats-event-city-row" key={city.id}>
                            <td className="stats-row-name">{cityTitle(city.name, city.start_at)}</td>
                            {manualCells(city)}
                        </tr>
                    ))}
            </React.Fragment>
        );
    };

    return (
        <div className="table-wrap">
            <table className="data-table stats-table buyer-type-table">
                <thead>
                <tr>
                    <th>Распространитель / мероприятие / город</th>
                    <th>Выдано мест</th>
                    <th>Продано мест</th>
                    <th>Продано на сумму</th>
                    <th>Не продано</th>
                    <th>Оплачено нам</th>
                </tr>
                </thead>
                <tbody>
                {segment.distributors.map((d) => {
                    const isOpen = open.has(d.id);
                    return (
                        <React.Fragment key={d.id}>
                            <tr className="stats-period-row">
                                <td className="stats-row-name">
                                    <div className="buyer-type-distributor-name">
                                        <button
                                            type="button"
                                            className={`stats-period-toggle ${isOpen ? "stats-period-toggle-open" : ""}`}
                                            onClick={() => setOpen((prev) => toggleInSet(prev, d.id))}
                                            title={d.email || undefined}
                                        >
                                            {d.name}
                                        </button>
                                        <Button
                                            type="button"
                                            variant="secondary"
                                            className="buyer-type-add-btn"
                                            onClick={() => onAddSold(d)}
                                        >
                                            Добавить проданные билеты
                                        </Button>
                                    </div>
                                </td>
                                {manualCells(d)}
                            </tr>
                            {isOpen &&
                                (d.tours.length === 0 ? (
                                    <tr className="stats-event-row">
                                        <td colSpan={6} className="stats-cell-muted">
                                            Билеты этому распространителю ещё не выдавались.
                                        </td>
                                    </tr>
                                ) : (
                                    d.tours.map((tour) => renderTour(d.id, tour))
                                ))}
                        </React.Fragment>
                    );
                })}
                </tbody>
            </table>
        </div>
    );
};

// ---------- Окно «Добавить проданные билеты» ----------

const SoldTicketsModal: React.FC<{
    distributor: ManualDistributorRow;
    onClose: (changed: boolean) => void;
}> = ({ distributor, onClose }) => {
    const [events, setEvents] = useState<ManualDistributorEvent[]>([]);
    const [eventsLoading, setEventsLoading] = useState(true);
    const [eventId, setEventId] = useState("");
    const [cityId, setCityId] = useState("");
    const [seats, setSeats] = useState<ManualDistributorSeat[]>([]);
    const [seatsLoading, setSeatsLoading] = useState(false);
    const [saving, setSaving] = useState<Set<string>>(new Set());
    const [error, setError] = useState<string | null>(null);
    const [changed, setChanged] = useState(false);

    useEffect(() => {
        setEventsLoading(true);
        getManualDistributorEventsRequest(distributor.id)
            .then((res) => setEvents(res.events))
            .catch((e) => setError(e?.message || "Не удалось загрузить мероприятия"))
            .finally(() => setEventsLoading(false));
    }, [distributor.id]);

    useEffect(() => {
        setSeats([]);
        if (!cityId) return;
        setSeatsLoading(true);
        setError(null);
        getManualDistributorSeatsRequest(distributor.id, cityId)
            .then((res) => setSeats(res.seats))
            .catch((e) => setError(e?.message || "Не удалось загрузить места"))
            .finally(() => setSeatsLoading(false));
    }, [distributor.id, cityId]);

    const selectedEvent = events.find((e) => e.id === eventId);

    // Ставит/снимает отметку у списка мест (одно место или «все»).
    const setPaid = async (ids: string[], paid: boolean) => {
        if (ids.length === 0) return;
        setError(null);
        setSaving((prev) => new Set([...Array.from(prev), ...ids]));
        try {
            await setManualDistributorSeatsPaidRequest(distributor.id, ids, paid);
            const idSet = new Set(ids);
            setSeats((prev) =>
                prev.map((s) =>
                    idSet.has(s.order_seat_id)
                        ? { ...s, distributor_paid: paid, distributor_paid_at: paid ? new Date().toISOString() : null }
                        : s
                )
            );
            setChanged(true);
        } catch (e: any) {
            setError(e?.message || "Не удалось сохранить отметку");
        } finally {
            setSaving((prev) => {
                const next = new Set(prev);
                ids.forEach((id) => next.delete(id));
                return next;
            });
        }
    };

    // Места заказов, уже оплаченных нам, считаются проданными и не отмечаются.
    const editable = seats.filter((s) => !s.order_is_paid);
    const soldCount = seats.filter((s) => s.distributor_paid || s.order_is_paid).length;
    const soldSum = seats
        .filter((s) => s.distributor_paid || s.order_is_paid)
        .reduce((acc, s) => acc + Number(s.price || 0), 0);

    return (
        <div className="modal-backdrop" role="dialog" aria-modal="true" aria-label="Добавить проданные билеты">
            <div className="modal buyer-type-modal">
                <h3 className="card-title">Проданные билеты — {distributor.name}</h3>
                <p className="card-text">
                    Отметка «Оплачено у распространителя» означает, что место продано и распространитель
                    отчитался. Заказ при этом не становится оплаченным — деньги нам ещё не поступили.
                </p>

                <div className="buyer-type-modal-selects">
                    <select
                        className="ui-select"
                        value={eventId}
                        disabled={eventsLoading}
                        onChange={(e) => {
                            setEventId(e.target.value);
                            setCityId("");
                        }}
                    >
                        <option value="">{eventsLoading ? "Загрузка…" : "Выберите мероприятие"}</option>
                        {events.map((ev) => (
                            <option key={ev.id} value={ev.id}>
                                {ev.name}
                            </option>
                        ))}
                    </select>
                    <select
                        className="ui-select"
                        value={cityId}
                        disabled={!selectedEvent}
                        onChange={(e) => setCityId(e.target.value)}
                    >
                        <option value="">Выберите город</option>
                        {selectedEvent?.cities.map((c) => (
                            <option key={c.id} value={c.id}>
                                {cityTitle(c.name, c.start_at)} — продано {c.sold_tickets} из {c.issued_tickets}
                            </option>
                        ))}
                    </select>
                </div>

                {!eventsLoading && events.length === 0 && (
                    <p className="stats-cell-muted">Этому распространителю ещё не выдавались билеты.</p>
                )}

                {error && <div className="form-global-error">{error}</div>}

                {cityId && (
                    <>
                        <div className="buyer-type-modal-toolbar">
                            <span>
                                Продано: <b>{soldCount}</b> из {seats.length} · {formatMoney(soldSum)}
                            </span>
                            <div className="buyer-type-modal-toolbar-actions">
                                <Button
                                    type="button"
                                    variant="secondary"
                                    disabled={seatsLoading || editable.every((s) => s.distributor_paid)}
                                    onClick={() =>
                                        setPaid(editable.filter((s) => !s.distributor_paid).map((s) => s.order_seat_id), true)
                                    }
                                >
                                    Отметить все
                                </Button>
                                <Button
                                    type="button"
                                    variant="ghost"
                                    disabled={seatsLoading || !editable.some((s) => s.distributor_paid)}
                                    onClick={() =>
                                        setPaid(editable.filter((s) => s.distributor_paid).map((s) => s.order_seat_id), false)
                                    }
                                >
                                    Снять все
                                </Button>
                            </div>
                        </div>

                        <div className="buyer-type-seats">
                            {seatsLoading ? (
                                <p className="stats-cell-muted">Загрузка мест…</p>
                            ) : seats.length === 0 ? (
                                <p className="stats-cell-muted">Мест нет.</p>
                            ) : (
                                seats.map((s) => {
                                    const busy = saving.has(s.order_seat_id);
                                    return (
                                        <label
                                            key={s.order_seat_id}
                                            className={`buyer-type-seat ${s.distributor_paid || s.order_is_paid ? "buyer-type-seat-paid" : ""}`}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={s.distributor_paid || s.order_is_paid}
                                                disabled={busy || s.order_is_paid}
                                                onChange={(e) => setPaid([s.order_seat_id], e.target.checked)}
                                            />
                                            <span className="buyer-type-seat-label">{s.label}</span>
                                            <span className="buyer-type-seat-meta">
                                                {formatMoney(s.price)}
                                                {Number(s.list_price) !== Number(s.price)
                                                    ? ` (по прайсу ${formatMoney(s.list_price)})`
                                                    : ""}{" "}
                                                · заказ {s.order_number}
                                            </span>
                                            <span className="buyer-type-seat-status">
                                                {s.order_is_paid
                                                    ? "Заказ оплачен нам"
                                                    : s.distributor_paid
                                                        ? "Оплачено у распространителя"
                                                        : "Не оплачено"}
                                            </span>
                                        </label>
                                    );
                                })
                            )}
                        </div>
                    </>
                )}

                <div className="editor-actions" style={{ marginTop: 20 }}>
                    <Button type="button" onClick={() => onClose(changed)}>
                        Готово
                    </Button>
                </div>
            </div>
        </div>
    );
};

// ---------- Вкладка целиком ----------

const BuyerTypeStatistics: React.FC = () => {
    const [data, setData] = useState<BuyerTypesStatisticsResponse | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [openSegments, setOpenSegments] = useState<Set<string>>(new Set());
    const [modalDistributor, setModalDistributor] = useState<ManualDistributorRow | null>(null);

    const load = useCallback(async (silent = false) => {
        if (!silent) setIsLoading(true);
        setError(null);
        try {
            setData(await getBuyerTypesStatisticsRequest());
        } catch (e: any) {
            setError(e?.message || "Не удалось загрузить статистику");
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    if (isLoading) {
        return (
            <div className="table-skeleton">
                <div className="table-skeleton-row" />
                <div className="table-skeleton-row" />
                <div className="table-skeleton-row" />
            </div>
        );
    }
    if (error || !data) {
        return <div className="form-global-error">{error || "Нет данных"}</div>;
    }

    const s = data.summary;
    const cards: { label: string; value: string | number }[] = [
        { label: "Продано мест", value: s.tickets_sold },
        { label: "На сумму", value: formatMoney(s.sum_amount) },
        { label: "Заказов", value: s.orders_count },
        { label: "Оплачено нам", value: formatMoney(s.paid_sum) },
        { label: "Не получено", value: formatMoney(s.unpaid_sum) },
        { label: "Средняя цена билета", value: formatMoney(s.average_ticket_price) },
        { label: "Выдано вручную", value: s.manual_issued_tickets },
        { label: "Продано ими", value: `${s.manual_sold_tickets} · ${formatMoney(s.manual_sold_sum)}` },
    ];

    return (
        <div className="buyer-type-stats">
            <div className="stats-summary-cards">
                {cards.map((c) => (
                    <div className="stats-summary-card" key={c.label}>
                        <div className="stats-summary-card-label">{c.label}</div>
                        <div className="stats-summary-card-value">{c.value}</div>
                    </div>
                ))}
            </div>

            {data.segments.map((segment, idx) => {
                const isOpen = openSegments.has(segment.key);
                const isManual = segment.key === "manual";
                return (
                    <div className="buyer-type-section" key={segment.key}>
                        <button
                            type="button"
                            className="buyer-type-section-header"
                            onClick={() => setOpenSegments((prev) => toggleInSet(prev, segment.key))}
                            aria-expanded={isOpen}
                        >
                            <span className={`stats-period-toggle ${isOpen ? "stats-period-toggle-open" : ""}`}>
                                {idx + 1}. {segment.label}
                            </span>
                            <span className="buyer-type-section-totals">
                                {isManual ? (
                                    <>
                                        выдано {(segment as BuyerTypeManualSegment).issued_tickets} · продано{" "}
                                        {segment.tickets_sold} мест · <b>{formatMoney(segment.sum_amount)}</b>
                                    </>
                                ) : (
                                    <>
                                        {segment.orders_count} заказов · {segment.tickets_sold} мест ·{" "}
                                        <b>{formatMoney(segment.sum_amount)}</b>
                                    </>
                                )}
                            </span>
                        </button>
                        {isOpen && (
                            <div className="buyer-type-section-body">
                                {isManual ? (
                                    <ManualDistributorsTable
                                        segment={segment as BuyerTypeManualSegment}
                                        onAddSold={setModalDistributor}
                                    />
                                ) : (segment as BuyerTypeRegularSegment).distributors ? (
                                    // Распространители и API: сначала список распространителей.
                                    <RegularDistributorsTable
                                        distributors={(segment as BuyerTypeRegularSegment).distributors || []}
                                    />
                                ) : (
                                    <RegularToursTable segment={segment as BuyerTypeRegularSegment} />
                                )}
                            </div>
                        )}
                    </div>
                );
            })}

            {modalDistributor && (
                <SoldTicketsModal
                    distributor={modalDistributor}
                    onClose={(changed) => {
                        setModalDistributor(null);
                        if (changed) load(true);
                    }}
                />
            )}
        </div>
    );
};

export default BuyerTypeStatistics;
